import React from 'react';

export interface VisualDestination {
    id: string;
    name: string;
    country: string;
    imageUrl: string;
    matchPercentage: number;
    bestSeason?: string;
    ecoScore?: number;
    aiReason?: string;
    price?: string;
    currency?: string;
    tags?: string[];
    vibe?: string; // e.g. "VIBE: MODERN"
    duration?: string; // e.g. "5 Days · Shopping & Skyline"
}

interface VisualSearchProps {
    destinations: VisualDestination[];
    onSelect?: (destination: VisualDestination) => void;
    preferences?: string;
    refinements?: string[];
    onRefine?: (refinement: string) => void;
    compact?: boolean;
}

export const TravelVisualSearch: React.FC<VisualSearchProps> = ({
    destinations,
    onSelect,
    preferences = "sun and culture",
    refinements = ["Lower budget", "More nature", "Closer to Stockholm"],
    onRefine,
    compact = false
}) => {
    if (!destinations || destinations.length === 0) return null;

    // Helper for fallback images
    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        e.currentTarget.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=800&auto=format&fit=crop';
    };

    if (compact) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {destinations.map((dest) => (
                    <div
                        key={dest.id}
                        onClick={() => onSelect && onSelect(dest)}
                        className="relative h-[450px] rounded-[2rem] overflow-hidden group cursor-pointer shadow-xl transition-all hover:-translate-y-1 hover:shadow-2xl"
                    >
                        {/* Image */}
                        <img
                            src={dest.imageUrl}
                            onError={handleImageError}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            alt={dest.name}
                        />

                        {/* Overlays */}
                        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

                        {/* Top Badges */}
                        <div className="absolute top-5 inset-x-5 flex justify-between items-start">
                            {dest.bestSeason && (
                                <div className="bg-white/10 backdrop-blur-md border border-white/20 text-white text-[10px] font-black px-3 py-1.5 rounded-lg uppercase tracking-wider">
                                    BEST IN {dest.bestSeason}
                                </div>
                            )}
                            <div className="bg-blue-600/90 text-white text-[10px] font-black px-3 py-1.5 rounded-lg uppercase tracking-wider shadow-lg flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span> {dest.matchPercentage}% Match
                            </div>
                        </div>

                        {/* Content */}
                        <div className="absolute inset-x-6 bottom-6 flex flex-col items-start gap-1">
                            {/* Vibe & Tags */}
                            <div className="flex flex-wrap gap-2 mb-2">
                                {dest.vibe && (
                                    <span className="bg-black/60 backdrop-blur-sm border border-white/10 text-white text-[9px] font-black px-2.5 py-1 rounded-md tracking-wider uppercase">
                                        VIBE: {dest.vibe}
                                    </span>
                                )}
                                {dest.tags?.map((t, i) => (
                                    <span key={i} className="bg-amber-500/90 text-slate-900 text-[9px] font-black px-2.5 py-1 rounded-md tracking-wider uppercase">
                                        {t}
                                    </span>
                                ))}
                            </div>

                            {/* Title */}
                            <h3 className="text-2xl font-bold text-white tracking-tight leading-tight group-hover:text-amber-400 transition-colors">
                                {dest.name}
                            </h3>

                            {/* Location */}
                            <p className="text-xs font-bold text-white/50 uppercase tracking-[0.2em] mb-4">
                                {dest.country}
                            </p>

                            {/* Details */}
                            {dest.duration && (
                                <div className="flex items-center gap-2 text-[10px] font-black text-amber-400 uppercase tracking-[0.1em] border-t border-white/10 pt-4 w-full">
                                    <MapPin size={12} className="text-amber-500" />
                                    {dest.duration}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const topPick = destinations[0];
    const alternatives = destinations.slice(1);

    return (
        <div className="w-full bg-slate-50 dark:bg-slate-900 rounded-3xl overflow-hidden font-sans transition-colors duration-300">
            {/* Immersive Hero Section (Top Pick) */}
            <div className="relative w-full h-[400px] md:h-[500px] group overflow-hidden md:rounded-3xl shadow-2xl mb-12">
                <img
                    src={topPick.imageUrl}
                    onError={handleImageError}
                    alt={topPick.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/20 to-transparent"></div>

                <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-center max-w-3xl">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-lg flex items-center gap-1">
                            ✨ AI Top Pick
                        </span>
                    </div>

                    <h1 className="text-4xl md:text-6xl font-serif text-white leading-tight mb-6 drop-shadow-lg">
                        {topPick.aiReason?.split('.')[0] || `Experience ${topPick.name}`}
                    </h1>

                    <div className="flex flex-wrap items-center gap-4 mb-8">
                        <Badge label={`${topPick.matchPercentage}% Match`} icon="🎯" color="bg-emerald-500/90" />
                        {topPick.bestSeason && <Badge label={`Best Season: ${topPick.bestSeason}`} icon="☀️" color="bg-slate-700/80" />}
                    </div>

                    <button
                        onClick={() => onSelect && onSelect(topPick)}
                        className="self-start bg-white text-slate-900 hover:bg-slate-100 font-bold py-4 px-8 rounded-xl shadow-xl transition-all flex items-center gap-2 group/btn"
                    >
                        Explore Full Itinerary
                    </button>
                </div>
            </div>
        </div>
    );
};

const Badge = ({ label, icon, color }: { label: string, icon: string, color: string }) => (
    <div className={`${color} backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-2 shadow-lg`}>
        <span>{icon}</span>
        {label}
    </div>
);

const MapPin = ({ size, className }: { size: number, className?: string }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
);
